import { useContext, useMemo } from 'react';
import { TSelectedDetail } from '../../../Types/MasterDetailTypes';
import { StandardRutinCollectionContext } from '../../../Features/Delsystem/_StandardRutin/StandardRutinCollectionContext';
import { selectedId } from '../MasterDetailUtils';
import { IClientBusinessModel } from '../../../Types/ClientModelTypes';

interface IDetailNavigation {
  selectNextDetail: ((force?: boolean) => void) | undefined;
  selectPreviousDetail: (() => void) | undefined;
  indexCaption: string;
  currentItem?: number;
  totalItems?: number;
}

/**
 * Hook that provides next/previous detail selection based on the currently
 * sorted/filtered rows in StandardRutinCollectionContext.
 */
export const useDetailNavigation = (
  selectedDetail: TSelectedDetail | undefined,
  selectDetail: (
    id: TSelectedDetail,
    force?: boolean,
    urlParams?: Record<string, string>
  ) => void,
  collection: IClientBusinessModel
): IDetailNavigation => {
  const collectionContext = useContext(StandardRutinCollectionContext);

  const currentIndex = useMemo(() => {
    const sortedList =
      collectionContext.filteredSortInfo.rows.length > 0
        ? collectionContext.filteredSortInfo
        : collectionContext.sortInfo;
    return sortedList.rows.findIndex(
      data => data.id === selectedId(selectedDetail)
    );
  }, [
    collectionContext.filteredSortInfo,
    collectionContext.sortInfo,
    selectedDetail,
  ]);

  const selectNextDetail = useMemo(() => {
    const sortedList =
      collectionContext.filteredSortInfo.rows.length > 0
        ? collectionContext.filteredSortInfo
        : collectionContext.sortInfo;

    if (!sortedList.rows || currentIndex === null) {
      return undefined;
    }
    const newIndex = currentIndex + 1;
    const newId = sortedList.rows[newIndex]?.id ?? null;
    const newNextId = sortedList.rows[newIndex + 1]?.id ?? null;
    const newPrevId = sortedList.rows[newIndex - 1]?.id ?? null;
    if (!newId) {
      return undefined;
    }
    return (force?: boolean) => {
      selectDetail(
        {
          id: newId,
          nextId: newNextId,
          prevId: newPrevId,
          data: sortedList.rows[newIndex],
        },
        force
      );
    };
  }, [
    collectionContext.filteredSortInfo,
    collectionContext.sortInfo,
    currentIndex,
    selectDetail,
  ]);

  const selectPreviousDetail = useMemo(() => {
    const sortedList =
      collectionContext.filteredSortInfo.rows.length > 0
        ? collectionContext.filteredSortInfo
        : collectionContext.sortInfo;

    if (!sortedList.rows || currentIndex === null) {
      return undefined;
    }
    const newIndex = currentIndex - 1;
    const newId = sortedList.rows[newIndex]?.id ?? null;
    const newNextId = sortedList.rows[newIndex + 1]?.id ?? null;
    const newPrevId = sortedList.rows[newIndex - 1]?.id ?? null;
    if (!newId) {
      return undefined;
    }
    return () => {
      selectDetail({
        id: newId,
        nextId: newNextId,
        prevId: newPrevId,
        data: sortedList.rows[newIndex],
      });
    };
  }, [
    collectionContext.filteredSortInfo,
    collectionContext.sortInfo,
    currentIndex,
    selectDetail,
  ]);

  const indexCaptionInfo = useMemo(() => {
    const sortedList =
      collectionContext.filteredSortInfo.rows.length > 0
        ? collectionContext.filteredSortInfo
        : collectionContext.sortInfo;
    if (currentIndex < 0 || !sortedList.rows[currentIndex]) {
      return {
        indexCaption: '',
        currentItem: undefined,
        totalItems: undefined,
      };
    }
    return {
      indexCaption: `${collection.RootType.SOLabel} ${currentIndex + 1} av ${
        sortedList.rows.length
      }`,
      currentItem: currentIndex + 1,
      totalItems: sortedList.rows.length,
    };
  }, [
    collectionContext.filteredSortInfo,
    collectionContext.sortInfo,
    currentIndex,
    collection.RootType.SOLabel,
  ]);

  return {
    selectNextDetail,
    selectPreviousDetail,
    indexCaption: indexCaptionInfo.indexCaption,
    currentItem: indexCaptionInfo.currentItem,
    totalItems: indexCaptionInfo.totalItems,
  };
};
